import { useEffect, useState } from 'react';
import { QuocGia, QuocGiaInput } from '@/types/schemas/quoc-gia.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface CountryFormattedData {
  ma_qg: string;
  ten_qg: string;
  ten_qg2?: string | null;
  status: string | number;
}

interface UseCountryReturn {
  countries: QuocGia[];
  isLoading: boolean;
  addCountry: (newCountry: CountryFormattedData) => Promise<void>;
  updateCountry: (uuid: string, updatedCountry: CountryFormattedData) => Promise<void>;
  deleteCountry: (uuid: string) => Promise<void>;
  refreshCountries: () => Promise<void>;
}

export const useCountry = (initialCountries: QuocGia[] = []): UseCountryReturn => {
  const [countries, setCountries] = useState<QuocGia[]>(initialCountries);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchCountries = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/entities/${entity.slug}/erp/countries/`);

      // API might return either data directly or in a results property
      const countriesData = response.data.results || response.data;
      setCountries(countriesData);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addCountry = async (newCountry: CountryFormattedData) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/entities/${entity.slug}/erp/countries/`, {
        ma_qg: newCountry.ma_qg,
        ten_qg: newCountry.ten_qg,
        ten_qg2: newCountry.ten_qg2 || null,
        status: newCountry.status !== undefined ? String(newCountry.status) : '1'
      });

      const addedCountry: QuocGia = response.data;
      setCountries(prev => [...prev, addedCountry]);
    } catch (error) {
      console.error('Error adding country:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCountry = async (uuid: string, updatedCountry: CountryFormattedData) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.patch(`/entities/${entity.slug}/erp/countries/${uuid}/`, {
        ma_qg: updatedCountry.ma_qg,
        ten_qg: updatedCountry.ten_qg,
        ten_qg2: updatedCountry.ten_qg2 || null,
        status: updatedCountry.status !== undefined ? String(updatedCountry.status) : '1'
      });

      // Update the country in the list
      const updatedCountryData: QuocGia = response.data;
      setCountries(prev =>
        prev.map(country => (country.uuid === updatedCountryData.uuid ? updatedCountryData : country))
      );
    } catch (error) {
      console.error('Error updating country:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCountry = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/countries/${uuid}/`);

      // Remove the country from the list
      setCountries(prev => prev.filter(country => country.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting country:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [entity?.slug]);

  return {
    countries,
    isLoading,
    addCountry,
    updateCountry,
    deleteCountry,
    refreshCountries: fetchCountries
  };
};
